import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";

const selectClass =
  "w-full bg-white border border-slate-300 px-3 py-2.5 text-sm rounded-sm text-slate-900 focus:outline-none focus:border-gold-600";

export default function Calculator() {
  const { lang, t } = useLanguage();
  const [location, setLocation] = useState(0);
  const [university, setUniversity] = useState(0);
  const [field, setField] = useState(0);
  const [device, setDevice] = useState(2);
  const [placements, setPlacements] = useState(50);
  const [weeks, setWeeks] = useState(4);

  const placementDays = placements * weeks * 7;
  const fmt = (n) => new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US").format(n);

  const locationName = t.calc.locations[location].split(" (")[0];

  const handleRequest = () => {
    const quota = {
      location: t.calc.locations[location],
      university: t.calc.universities[university],
      field: t.calc.fields[field],
      device: t.calc.devices[device],
      placements,
      weeks,
    };
    sessionStorage.setItem("lidlink-quota", JSON.stringify(quota));
    window.dispatchEvent(new CustomEvent("lidlink:quota", { detail: quota }));
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="calculator" className="py-20 bg-white border-b spec-border" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="max-w-3xl mb-12">
          <span className="text-xs font-mono-spec text-gold-700 font-semibold tracking-wider uppercase">{t.calc.eyebrow}</span>
          <h2 className="text-3xl font-bold tracking-tight text-slate-950 mt-2">{t.calc.title}</h2>
          <p className="text-slate-600 mt-2 text-base">{t.calc.sub}</p>
        </div>

        <div className="bg-slate-50 border spec-border p-6 sm:p-8 rounded-sm grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono-spec font-medium text-slate-700 mb-1">{t.calc.location}</label>
                <select
                  data-testid="calc-location"
                  className={selectClass}
                  value={location}
                  onChange={(e) => setLocation(Number(e.target.value))}
                >
                  {t.calc.locations.map((l, i) => (
                    <option key={l} value={i}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono-spec font-medium text-slate-700 mb-1">{t.calc.university}</label>
                <select
                  data-testid="calc-university"
                  className={selectClass}
                  value={university}
                  onChange={(e) => setUniversity(Number(e.target.value))}
                >
                  {t.calc.universities.map((u, i) => (
                    <option key={u} value={i}>{u}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono-spec font-medium text-slate-700 mb-1">{t.calc.field}</label>
                <select
                  data-testid="calc-field"
                  className={selectClass}
                  value={field}
                  onChange={(e) => setField(Number(e.target.value))}
                >
                  {t.calc.fields.map((f, i) => (
                    <option key={f} value={i}>{f}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono-spec font-medium text-slate-700 mb-1">{t.calc.device}</label>
                <select
                  data-testid="calc-device"
                  className={selectClass}
                  value={device}
                  onChange={(e) => setDevice(Number(e.target.value))}
                >
                  {t.calc.devices.map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono-spec font-medium text-slate-700 mb-1">{t.calc.placements}</label>
                <select
                  data-testid="calc-placements"
                  className={selectClass}
                  value={placements}
                  onChange={(e) => setPlacements(Number(e.target.value))}
                >
                  {t.calc.placementOptions.map((p) => (
                    <option key={p} value={p}>{p} {t.calc.placementSuffix}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono-spec font-medium text-slate-700 mb-1">{t.calc.duration}</label>
                <select
                  data-testid="calc-duration"
                  className={selectClass}
                  value={weeks}
                  onChange={(e) => setWeeks(Number(e.target.value))}
                >
                  {t.calc.durationOptions.map((d) => (
                    <option key={d.weeks} value={d.weeks}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-4 bg-white border spec-border text-xs text-slate-600 space-y-1">
              <div className="font-semibold text-slate-900 font-mono-spec">{t.calc.includedTitle}</div>
              {t.calc.included.map((item) => (
                <div key={item}>✓ {item}</div>
              ))}
            </div>
          </div>

          {/* Configuration summary */}
          <div className="lg:col-span-5 bg-slate-950 text-white p-6 rounded-sm flex flex-col justify-between">
            <div>
              <div className="text-xs font-mono-spec text-gold-300">{t.calc.estimateLabel}</div>
              <div className="mt-4 border-b border-slate-800 pb-4">
                <div className="text-xl font-bold font-mono-spec" data-testid="calc-summary">
                  {placements} {t.calc.devicesUnit} · {weeks} {t.calc.weeksUnit} · {locationName}
                </div>
                <div className="text-xs text-slate-400 mt-1 font-mono-spec" data-testid="calc-summary-2">
                  {t.calc.universities[university]} · {t.calc.fields[field]}
                </div>
              </div>
              <div className="py-4 space-y-2 text-xs font-mono-spec text-slate-300">
                <div className="flex justify-between gap-4">
                  <span>{t.calc.placementDays}</span>
                  <span className="text-white font-bold" data-testid="calc-placement-days">{fmt(placementDays)} {t.calc.daysUnit}</span>
                </div>
                <div className="text-[10px] text-slate-500 -mt-1 pb-1">{t.calc.placementDaysHint}</div>
                <div className="flex justify-between">
                  <span>{t.calc.verification}</span>
                  <span className="text-emerald-400">{t.calc.verificationValue}</span>
                </div>
                <div className="flex justify-between border-t border-slate-800 pt-3 mt-3">
                  <span>{t.calc.pricingLabel}</span>
                  <span className="text-gold-300 font-bold" data-testid="calc-pricing">{t.calc.pricingValue}</span>
                </div>
                <div className="text-[10px] text-slate-500">{t.calc.pricingHint}</div>
              </div>
            </div>

            <button
              data-testid="calc-request-btn"
              onClick={handleRequest}
              className="w-full text-center py-3 bg-gold-600 hover:bg-gold-500 text-white font-mono-spec text-xs font-bold uppercase tracking-wider rounded-sm transition-colors"
            >
              {t.calc.cta}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
